/**
 * PGMQ Webhook Delivery Enqueue
 *
 * Shared helper for publishing webhook_delivery_requested events to PGMQ.
 * Used by WebhookBridgeConnector and IntegrationActivityService.
 *
 * @module mycelium-webhook-enqueue
 * @see docs/protocols/mycelium/WEBHOOK_ELIMINATION_PLAYBOOK.md
 */

import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";

/** Shape of the webhook delivery event published to PGMQ */
export interface WebhookDeliveryEvent {
  /** Original event domain */
  sourceDomain: string;
  /** Original event type */
  sourceType: string;
  /** Original event id */
  sourceEventId: string;
  /** Organization to look up webhooks for */
  orgId: string;
  /** Full event payload for webhook body */
  eventPayload: Record<string, unknown>;
  /** Timestamp of the original event */
  originalTimestamp: string;
}

export interface EnqueueWebhookDeliveryParams extends WebhookDeliveryEvent {
  /** Caller identifier for tracing (e.g. WebhookBridgeConnector, IntegrationActivityService) */
  source: string;
  userId?: string;
}

/**
 * Publish a webhook_delivery_requested event to PGMQ for async HTTP delivery
 * by mycelium-worker. Fire-and-forget — logs warnings on failure, never throws.
 */
export async function enqueueWebhookDelivery(
  params: EnqueueWebhookDeliveryParams
): Promise<void> {
  const {
    sourceDomain,
    sourceType,
    sourceEventId,
    orgId,
    eventPayload,
    originalTimestamp,
    source,
    userId,
  } = params;

  const deliveryPayload: WebhookDeliveryEvent = {
    sourceDomain,
    sourceType,
    sourceEventId,
    orgId,
    eventPayload,
    originalTimestamp,
  };

  try {
    const result = await getMyceliumTransport().publish({
      domain: "webhook",
      type: "webhook_delivery_requested",
      orgId,
      userId,
      payload: {
        ...deliveryPayload,
        source,
      },
    });

    if (!result.success) {
      logger.warn("Webhook enqueue: failed to publish delivery event", {
        source,
        sourceEvent: `${sourceDomain}.${sourceType}`,
        orgId,
        error: result.error,
      });
    } else {
      logger.debug("Webhook enqueue: delivery event published", {
        source,
        sourceEvent: `${sourceDomain}.${sourceType}`,
        orgId,
        messageId: result.data,
      });
    }
  } catch (error) {
    logger.error("Webhook enqueue: error publishing delivery event", {
      source,
      sourceEvent: `${sourceDomain}.${sourceType}`,
      orgId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
